import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Progress } from "../components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { StatusBadge } from "../components/badges";
import { toast } from "sonner";
import { Plus } from "lucide-react";

export default function Projects() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", key: "", description: "", start_date: "", end_date: "" });

  const load = () => api.get("/projects").then((r) => setProjects(r.data));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/projects", form);
      toast.success("Project created");
      setOpen(false);
      setForm({ name: "", key: "", description: "", start_date: "", end_date: "" });
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const canCreate = user?.role === "admin" || user?.role === "manager";

  return (
    <div className="space-y-6 fade-in" data-testid="projects-page">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Portfolio</p>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Projects</h1>
        </div>
        {canCreate && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-project-btn"><Plus className="h-4 w-4 mr-1" /> New Project</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-heading">New Project</DialogTitle></DialogHeader>
              <form onSubmit={create} className="space-y-4">
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-2 space-y-1.5">
                    <Label>Name</Label>
                    <Input data-testid="project-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Key</Label>
                    <Input data-testid="project-key-input" placeholder="AERO" maxLength={6} value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value.toUpperCase() })} required />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Description</Label>
                  <Textarea data-testid="project-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Start date</Label>
                    <Input type="date" data-testid="project-start-input" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>End date</Label>
                    <Input type="date" data-testid="project-end-input" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
                  </div>
                </div>
                <Button type="submit" data-testid="project-create-submit-btn" className="w-full">Create Project</Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4 stagger">
        {projects.map((p) => (
          <Link key={p.id} to={`/projects/${p.id}`} data-testid={`project-grid-card-${p.key.toLowerCase()}`}>
            <Card className="p-6 h-full hover:border-primary/50 transition-colors flex flex-col">
              <div className="flex items-center gap-2">
                <span className="font-mono text-xs text-primary">{p.key}</span>
                <StatusBadge status={p.status} />
              </div>
              <h3 className="font-heading font-semibold text-lg mt-2">{p.name}</h3>
              <p className="text-sm text-muted-foreground line-clamp-2 mt-1 flex-1">{p.description}</p>
              <div className="mt-4">
                <div className="flex justify-between text-xs font-mono text-muted-foreground mb-1.5">
                  <span>{p.completed_count}/{p.task_count} tasks · {p.open_issues} open issues</span>
                  <span>{p.progress}%</span>
                </div>
                <Progress value={p.progress} className="h-1.5" />
                <p className="text-[10px] font-mono text-muted-foreground mt-2">{p.start_date} → {p.end_date}</p>
              </div>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  );
}
